<template>
  <div>
    <p class="text-body text-grey-text-subdued">Optional</p>
    <h2 class="text-h2 mt-24">You mentioned you had a pet.</h2>
    <p class="text-body text-grey-body mt-16">
      Add a few quick details so sitters know what to expect and can feel confident providing the care you need.
    </p>
    <Tabs
      v-model="activePetIndex"
      :tabs="tabs"
      class="mt-24"
      :showAddTabAction="true"
      addTabLabel="Add pet"
      :maxTabs="maxTabs"
      @add-tab="addPetTab"
    />
    <div class="flex flex-row-reverse mt-48">
      <RouteStepperNextButton ref="routeStepperNextButton" :draftData="draftData" />
    </div>
  </div>
</template>

<script lang="ts">
import Tabs from '@/components/tabs/Tabs.vue';
import RouteStepperNextButton from '@/components/route_stepper/RouteStepperNextButton.vue';
import PetDetailsForm from './pet_details/PetDetailsForm.vue';
import { JobPostDraft } from './types';

export default {
  name: 'JobPostPetDetails',
  components: { Tabs, RouteStepperNextButton },
  props: {
    draft: {
      type: Object,
      required: true,
    },
  },
  data() {
    const initialPets = this.draft?.pets && this.draft.pets.length > 0 ? [...this.draft.pets] : [{ id: `pet-${Date.now()}` }];
    return {
      activePetIndex: 0,
      pets: initialPets,
      maxTabs: 3,
    };
  },
  computed: {
    draftData(): JobPostDraft {
      // Clean up pet data by removing empty fields and only including pets with a valid petType
      const petDetails = this.pets.map(this.sanitizePetData).filter((pet) => Boolean(pet.petType));

      return {
        petDetails: petDetails.length > 0 ? petDetails : undefined,
      };
    },
    tabs() {
      return this.pets.map((pet, index) => ({
        key: pet.id,
        title: `Pet ${index + 1}`,
        component: PetDetailsForm,
        props: {
          petData: pet,
          petIndex: index,
          petCount: this.pets.length,
          activePetIndex: this.activePetIndex,
        },
        listeners: {
          'remove-pet': () => this.removePetTab(index),
          'update-pet': (updatedPet) => this.updatePet(index, updatedPet),
        },
      }));
    },
  },
  methods: {
    addPetTab() {
      if (this.pets.length < this.maxTabs) {
        this.pets.push({ id: `pet-${Date.now()}` });
        this.activePetIndex = this.pets.length - 1;
      }
    },
    removePetTab(index: number) {
      this.pets.splice(index, 1);
      if (this.activePetIndex >= this.pets.length && this.pets.length > 0) {
        this.activePetIndex = this.pets.length - 1;
      }
    },
    updatePet(index: number, updatedPet: any) {
      this.$set(this.pets, index, { ...this.pets[index], ...updatedPet });
    },
    sanitizePetData({ id, ...pet }: any): any {
      return Object.fromEntries(
        Object.entries(pet).filter(([, value]) => (Array.isArray(value) ? value.length > 0 : value != null && value !== ''))
      );
    },
  },
};
</script>
